import { Button, CalloutBanner, Icon, Popover, TextField } from '@alto-avios/alto-ui'
import QuantityStepper from '~/components/shared/QuantityStepper'
import { FlightSearchFormData, TravellersFormData } from '@reward-platform/flights-schemas'
import MobileModal from '@reward-platform/lift/components/MobileModal'
import { useTranslations } from 'next-intl'
import { MutableRefObject, useCallback, useMemo, useRef, useState } from 'react'
import { useOverlayTrigger } from '@react-aria/overlays'
import { useForm } from 'react-hook-form'
import { useOverlayTriggerState } from 'react-stately'
import { ChildrenAgePicker } from '~/components/shared/ChildrenAgePicker'
import { updateAgeOfChild, updateChildrenAges } from '~/components/shared/ChildrenAgePicker/utils'
import { TravelersQuantity } from '~/components/shared/TravelerQuantityPicker'
import { useDeviceMatch } from '~/context/mediaQuery'
import { useFormatFlightTravellers } from '~/hooks/useFormatFlightTravellers/useFormatFlightTravellers'
import useBasket from '~/hooks/useBasket/useBasket'
import { Errors, validatePassengers } from './FlightTravellerInput.utils'
import { MAX_TRAVELLERS } from './constants'
import style from './FlightTravellerInput.module.scss'

type Props = {
  name: string
  onChange: (value: TravellersFormData) => void
  disabled?: boolean
  value: TravellersFormData
}

export function FlightTravellerInput({ name, onChange, disabled, value }: Props) {
  const wrapper = useRef<HTMLInputElement | null>(null)
  const [open, setOpen] = useState<boolean>(false)
  const state = useOverlayTriggerState({ isOpen: open })
  const { overlayProps } = useOverlayTrigger({ type: 'dialog' }, state, wrapper)
  const { isDesktop } = useDeviceMatch()

  const { basket } = useBasket()
  const hotelInBasket = !!basket?.data?.items.HOTEL?.length
  const { setError } = useForm<FlightSearchFormData>()
  const error = useMemo(() => validatePassengers(value), [value])
  const invalid = !!error

  const placeholder = useFormatFlightTravellers({
    adult: value?.adult.quantity ?? 1,
    child: value?.child.quantity ?? 0,
  })

  const t = useTranslations('pages.flights.search_form.travellers')

  const errorMessage = useMemo(() => {
    switch (error) {
      case Errors.InfantsExceedingAdults:
        return t('infants-exceeding-adults')
      case Errors.NoAdultPresentForChild: {
        return t('no-adult-present-for-child')
      }
      default:
        return undefined
    }
  }, [error, t])

  const handleTravellerQuantityChange = (travellerQuantity: TravelersQuantity) => {
    // compare with the previous state and call update function if children count changed
    const didChildChange = value.childrenAges.length !== travellerQuantity.child.quantity

    const travellerInput: TravellersFormData = {
      ...travellerQuantity,
      childrenAges: didChildChange
        ? updateChildrenAges(value.childrenAges, travellerQuantity.child.quantity) ?? []
        : value.childrenAges,
    }

    const errors = validatePassengers(travellerInput)

    if (errors) {
      setError('travellers', { type: 'travellers' })
    }
    onChange(travellerInput)
  }

  const closePopover = useCallback(() => setOpen(false), [setOpen])

  const Picker = (
    <div className={style.picker_wrapper}>
      <div className={style.passengers_inputs} data-testid="travel-picker-container">
        {isDesktop && (
          <div className={style.top_row}>
            <button
              type="button"
              onClick={closePopover}
              data-testid="travellers-close"
              title="Close"
            >
              &#x2715;
            </button>
          </div>
        )}

        <div className={style.wrapper}>
          {value.adult.quantity + value.child.quantity >= MAX_TRAVELLERS && (
            <div className={style.centered}>
              <CalloutBanner
                title=""
                styleVariant="neutral"
                emphasis="secondary"
                hideIllustration
                description={t('max-travellers', { maxTravellers: MAX_TRAVELLERS })}
              />
            </div>
          )}

          {hotelInBasket && (
            <CalloutBanner
              title=""
              styleVariant="information"
              description={t('same-travellers')}
              emphasis="secondary"
            />
          )}

          <QuantityStepper
            value={String(value.adult.quantity)}
            label="Adults"
            isRequired
            minValue={1}
            maxValue={MAX_TRAVELLERS - value.child.quantity}
            defaultValue="1"
            errorMessage={errorMessage}
            errorPlacement="top"
            onChange={(quantity: string | number) => {
              handleTravellerQuantityChange({
                senior: { quantity: 0 },
                infant: { quantity: 0 },
                youth: { quantity: 0 },
                traveler: { quantity: 0 },
                ...value,
                adult: { quantity: Number(quantity) },
              })
            }}
          />

          <QuantityStepper
            value={String(value.child.quantity)}
            label="Children"
            description="(Aged 0 to 17)"
            isRequired
            defaultValue="0"
            minValue={0}
            maxValue={MAX_TRAVELLERS - value.adult.quantity}
            onChange={(quantity: string | number) => {
              handleTravellerQuantityChange({
                senior: { quantity: 0 },
                infant: { quantity: 0 },
                youth: { quantity: 0 },
                traveler: { quantity: 0 },
                ...value,
                child: { quantity: Number(quantity) },
              })
            }}
          />

          {value.child.quantity > 0 && (
            <>
              <ChildrenAgePicker
                childrenAges={value.childrenAges}
                includeUnderOne
                handleSelect={({ child, age }) => {
                  const childIndex = +child.split('_')[1] // relies on key being in 'child_<Number>' format
                  const travellerInput: TravellersFormData = {
                    ...value,
                    childrenAges: updateAgeOfChild(value.childrenAges, { child, age }, childIndex),
                  }

                  onChange(travellerInput)
                }}
              />

              {value.childrenAges.length >= 1 && (
                <CalloutBanner
                  title=""
                  styleVariant="information"
                  emphasis="secondary"
                  description={t('correct-age')}
                />
              )}
            </>
          )}

          <Button
            onClick={closePopover}
            styleVariant="accent"
            emphasis="primary"
            size="sm"
            fullWidth
            isDisabled={!!error}
          >
            {t('done')}
          </Button>
        </div>
      </div>
    </div>
  )

  const Input = (
    <TextField
      ref={wrapper}
      id="dropdown-traveller-input"
      data-testid="dropdown-traveller-input"
      name={name}
      label={t('travellers-label')}
      isReadOnly
      isRequired
      isDisabled={disabled}
      isInvalid={invalid}
      onFocus={(e) => {
        e.target.blur()
        setOpen((isOpen) => !isOpen)
      }}
      errorMessage={errorMessage}
      placeholder={placeholder}
      value={placeholder}
    >
      <TextField.StartIcon>
        <Icon iconName="user" iconSize="1x" isDecorative />
      </TextField.StartIcon>
      <TextField.EndIcon>
        <Icon iconName="angles-up-down" iconSize="1x" isDecorative />
      </TextField.EndIcon>
    </TextField>
  )

  return (
    <>
      {Input}
      {isDesktop ? (
        state.isOpen && (
          <Popover
            {...overlayProps}
            state={state}
            triggerRef={wrapper as MutableRefObject<HTMLButtonElement | null>}
            placement="bottom"
            hasArrow={false}
            autoFocus
            allowTabOut
            isNonModal
          >
            {Picker}
          </Popover>
        )
      ) : (
        <MobileModal
          id="travellerModal"
          isModalVisible={state.isOpen}
          handleModalClose={closePopover}
          forceModal
        >
          {state.isOpen && Picker}
        </MobileModal>
      )}
    </>
  )
}
